/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. The file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function () {
  // Announcement bar
  var SHOPPY_ANNOUNCEMENT_CAROUSEL_MAX_PX = 1200;

  function shoppyAnnouncementUseCarousel() {
    return window.innerWidth <= SHOPPY_ANNOUNCEMENT_CAROUSEL_MAX_PX;
  }

  function shoppySetAnnouncementBarHeight() {
    var bar = document.querySelector(".shopify-section--announcement-bar");
    if (!bar) {
      document.documentElement.style.setProperty(
        "--announcement-bar-height",
        "0px",
      );
      return;
    }
    document.documentElement.style.setProperty(
      "--announcement-bar-height",
      bar.clientHeight + "px",
    );
  }

  function shoppyInitAnnouncementBarHeight() {
    shoppySetAnnouncementBarHeight();
    var bar = document.querySelector(".shopify-section--announcement-bar");
    if (bar && typeof ResizeObserver !== "undefined") {
      new ResizeObserver(shoppySetAnnouncementBarHeight).observe(bar);
    }
    window.addEventListener("resize", shoppySetAnnouncementBarHeight);
  }

  function shoppyDestroyAnnouncementSplide(splideRoot) {
    if (!splideRoot || !splideRoot._shoppySplideInstance) {
      return;
    }
    try {
      splideRoot._shoppySplideInstance.destroy(true);
    } catch (e) {}
    splideRoot._shoppySplideInstance = null;
  }

  function shoppyMountAnnouncementSplide() {
    var splideRoot = document.querySelector(".shoppy-announcement-bar__splide");
    if (!splideRoot || typeof Splide === "undefined") {
      return;
    }

    function startCarouselIfNeeded() {
      if (splideRoot._shoppySplideInstance) {
        return;
      }

      var autoplay =
        splideRoot.getAttribute("data-shoppy-autoplay") !== "false";
      var intervalMs = parseInt(
        splideRoot.getAttribute("data-shoppy-interval") || "5000",
        10,
      );
      var fadeMs = parseInt(
        splideRoot.getAttribute("data-shoppy-speed") || "500",
        10,
      );

      var instance = new Splide(splideRoot, {
        type: "fade",
        rewind: true,
        perPage: 1,
        gap: 0,
        speed: fadeMs,
        arrows: false,
        pagination: false,
        drag: true,
        autoplay: autoplay,
        interval: intervalMs,
        pauseOnHover: true,
      });

      instance.on(
        "mounted updated resize moved",
        shoppySetAnnouncementBarHeight,
      );
      instance.mount();
      splideRoot._shoppySplideInstance = instance;
      shoppySetAnnouncementBarHeight();
    }

    function applyLayoutForCurrentWidth() {
      if (shoppyAnnouncementUseCarousel()) {
        startCarouselIfNeeded();
      } else {
        shoppyDestroyAnnouncementSplide(splideRoot);
        shoppySetAnnouncementBarHeight();
      }
    }

    if (!splideRoot._shoppyAnnouncementLayoutBound) {
      var resizeRaf = null;
      function onViewportChange() {
        if (resizeRaf != null) {
          cancelAnimationFrame(resizeRaf);
        }
        resizeRaf = requestAnimationFrame(function () {
          resizeRaf = null;
          applyLayoutForCurrentWidth();
        });
      }
      window.addEventListener("resize", onViewportChange);
      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", onViewportChange);
      }
      splideRoot._shoppyAnnouncementLayoutBound = true;
    }

    applyLayoutForCurrentWidth();
  }

  function shoppyInitAnnouncementBar() {
    shoppyInitAnnouncementBarHeight();
    shoppyMountAnnouncementSplide();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", shoppyInitAnnouncementBar);
  } else {
    shoppyInitAnnouncementBar();
  }

  document.addEventListener("shopify:section:load", function (event) {
    var sectionEl = document.getElementById(
      "shopify-section-" + event.detail.sectionId,
    );
    if (
      sectionEl &&
      sectionEl.classList.contains("shopify-section--announcement-bar")
    ) {
      shoppyMountAnnouncementSplide();
      shoppySetAnnouncementBarHeight();
    }
  });
})();

(function () {
  // =====================================================================================
  // Shoppy Splide helpers
  // =====================================================================================
  var SHOPPY_SPLIDE_EVENTS =
    "mounted moved refresh scrolled resized updated dragged";

  function shoppyGetRtlDirection() {
    return document.documentElement.getAttribute("dir") === "rtl"
      ? "rtl"
      : "ltr";
  }

  function shoppyDestroySplide(root) {
    if (!root || !root._shoppySplideInstance) {
      return;
    }

    try {
      root._shoppySplideInstance.destroy(true);
    } catch (e) {}

    root._shoppySplideInstance = null;
  }

  function shoppyCleanupSplideMql(root) {
    if (!root || !root._shoppySplideMql || !root._shoppySplideMqlHandler) {
      return;
    }

    try {
      if (typeof root._shoppySplideMql.removeEventListener === "function") {
        root._shoppySplideMql.removeEventListener(
          "change",
          root._shoppySplideMqlHandler,
        );
      } else if (typeof root._shoppySplideMql.removeListener === "function") {
        root._shoppySplideMql.removeListener(root._shoppySplideMqlHandler);
      }
    } catch (e) {}

    root._shoppySplideMql = null;
    root._shoppySplideMqlHandler = null;
  }

  function shoppyUpdateNavByWrapperId(splide, rootId) {
    var wrap = document.querySelector(
      '[data-shoppy-splide-id="' + rootId + '"]',
    );

    if (!wrap) {
      return;
    }

    var prev =
      wrap.querySelector(".prev-next-button--prev") ||
      wrap.querySelector("button[aria-label*='Previous']");
    var next =
      wrap.querySelector(".prev-next-button--next") ||
      wrap.querySelector("button[aria-label*='Next']");

    if (!prev || !next) {
      return;
    }

    var ctrl = splide.Components.Controller;
    if (!ctrl || typeof ctrl.getNext !== "function") {
      return;
    }

    var idx = splide.index;
    var nextIndex = ctrl.getNext();
    var prevIndex = ctrl.getPrev();

    // autoWidth edge case: can return current index instead of -1.
    if (nextIndex === idx) {
      nextIndex = -1;
    }
    if (prevIndex === idx) {
      prevIndex = -1;
    }

    next.disabled = nextIndex < 0;
    prev.disabled = prevIndex < 0;
  }

  function shoppyMountAutoWidthSplide(root, options) {
    if (!root || typeof Splide === "undefined") {
      return;
    }

    if (!root.id) {
      return;
    }

    // defensive: always destroy before mount
    shoppyDestroySplide(root);

    var splide = new Splide(root, options);

    splide.on(SHOPPY_SPLIDE_EVENTS, function () {
      shoppyUpdateNavByWrapperId(splide, root.id);
    });

    splide.mount();
    root._shoppySplideInstance = splide;

    shoppyUpdateNavByWrapperId(splide, root.id);

    requestAnimationFrame(function () {
      if (root._shoppySplideInstance) {
        root._shoppySplideInstance.refresh();
        shoppyUpdateNavByWrapperId(root._shoppySplideInstance, root.id);
      }
    });
  }

  function shoppyInitCollectionListSplide(scope) {
    (scope || document)
      .querySelectorAll(".shoppy-collection-list__splide")
      .forEach(function (root) {
        var mountMode = root.getAttribute("data-shoppy-mount") || "";

        if (mountMode === "desktop") {
          var mql = window.matchMedia("(min-width: 741px)");

          if (!root._shoppySplideMql) {
            root._shoppySplideMql = mql;
            root._shoppySplideMqlHandler = function () {
              if (root._shoppySplideMql.matches) {
                shoppyInitCollectionListSplide(root.parentNode || document);
              } else {
                shoppyDestroySplide(root);
              }
            };

            try {
              if (typeof mql.addEventListener === "function") {
                mql.addEventListener("change", root._shoppySplideMqlHandler);
              } else if (typeof mql.addListener === "function") {
                mql.addListener(root._shoppySplideMqlHandler);
              }
            } catch (e) {}
          }

          if (!mql.matches) {
            shoppyDestroySplide(root);
            return;
          }
        }

        var gap = parseInt(root.getAttribute("data-shoppy-gap") || "16", 10);

        shoppyMountAutoWidthSplide(root, {
          type: "slide",
          autoWidth: true,
          rewind: false,
          omitEnd: true,
          perMove: 1,
          gap: gap + "px",
          pagination: false,
          arrows: false,
          drag: true,
          direction: shoppyGetRtlDirection(),
        });
      });
  }

  function shoppyFeaturedSyncHeaderArrows() {
    document
      .querySelectorAll(".shoppy .product-list[id^='block-']")
      .forEach(function (pl) {
        var headerArrows = document.querySelector(
          '[data-shoppy-arrows-for="' + pl.id + '"]',
        );

        if (headerArrows) {
          headerArrows.toggleAttribute("hidden", pl.hidden);
        }
      });
  }

  function shoppyMountFeaturedSplide(root) {
    if (!root) {
      return;
    }

    var pl = root.closest(".product-list");

    if (pl && pl.hidden) {
      shoppyDestroySplide(root);
      return;
    }

    shoppyMountAutoWidthSplide(root, {
      type: "slide",
      autoWidth: true,
      rewind: false,
      omitEnd: true,
      perMove: 1,
      gap:
        (parseInt(root.getAttribute("data-shoppy-gap") || "24", 10) || 24) +
        "px",
      pagination: false,
      arrows: false,
      drag: true,
      direction: shoppyGetRtlDirection(),
      breakpoints: {
        740: { gap: "12px" },
      },
    });
  }

  function shoppyFeaturedBindProductLists(scope) {
    (scope || document)
      .querySelectorAll(".shoppy .product-list[id^='block-']")
      .forEach(function (pl) {
        if (pl._shoppyFeaturedObserved) {
          return;
        }

        pl._shoppyFeaturedObserved = true;

        var obs = new MutationObserver(function () {
          shoppyFeaturedSyncHeaderArrows();

          var root = pl.querySelector(".shoppy-featured-collections__splide");
          if (!root) {
            return;
          }

          if (pl.hidden) {
            shoppyDestroySplide(root);
          } else {
            shoppyMountFeaturedSplide(root);
          }
        });

        obs.observe(pl, { attributes: true, attributeFilter: ["hidden"] });
      });
  }

  function shoppyFeaturedInitAll(scope) {
    var context = scope || document;

    shoppyFeaturedBindProductLists(context);
    shoppyFeaturedSyncHeaderArrows();

    context
      .querySelectorAll(".shoppy .shoppy-featured-collections__splide")
      .forEach(function (root) {
        shoppyMountFeaturedSplide(root);
      });
  }

  document.addEventListener("click", function (e) {
    var wrap = e.target.closest("[data-shoppy-splide-id]");
    if (!wrap) {
      return;
    }

    var prevBtn = e.target.closest(".prev-next-button--prev");
    var nextBtn = e.target.closest(".prev-next-button--next");
    if (!prevBtn && !nextBtn) {
      return;
    }

    var root = document.getElementById(
      wrap.getAttribute("data-shoppy-splide-id"),
    );

    if (!root || !root._shoppySplideInstance) {
      return;
    }

    var splide = root._shoppySplideInstance;
    var ctrl = splide.Components.Controller;

    e.preventDefault();

    if (prevBtn) {
      if (!ctrl || ctrl.getPrev() < 0) {
        return;
      }
      ctrl.go("<", true);
    } else {
      if (!ctrl || ctrl.getNext() < 0) {
        return;
      }
      ctrl.go(">", true);
    }

    requestAnimationFrame(function () {
      if (root._shoppySplideInstance) {
        shoppyUpdateNavByWrapperId(root._shoppySplideInstance, root.id);
      }
    });
  });

  function shoppyInitAll(scope) {
    shoppyFeaturedInitAll(scope || document);
    shoppyInitCollectionListSplide(scope || document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      shoppyInitAll(document);
    });
  } else {
    shoppyInitAll(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    var sectionEl = event.target;
    if (!sectionEl) {
      return;
    }

    sectionEl.querySelectorAll(".splide").forEach(function (root) {
      shoppyDestroySplide(root);
    });

    shoppyFeaturedInitAll(sectionEl);
    shoppyInitCollectionListSplide(sectionEl);
  });

  document.addEventListener("shopify:section:reorder", function (event) {
    var sectionEl = event.target;
    if (!sectionEl) {
      return;
    }

    sectionEl.querySelectorAll(".splide").forEach(function (root) {
      if (root._shoppySplideInstance) {
        root._shoppySplideInstance.refresh();
      } else {
        if (root.matches(".shoppy-featured-collections__splide")) {
          shoppyMountFeaturedSplide(root);
        } else if (root.matches(".shoppy-collection-list__splide")) {
          shoppyInitCollectionListSplide(sectionEl);
        }
      }
    });
  });

  document.addEventListener("shopify:section:unload", function (event) {
    var sectionEl = event.target;
    if (!sectionEl) {
      return;
    }

    sectionEl.querySelectorAll(".splide").forEach(function (root) {
      shoppyDestroySplide(root);
      shoppyCleanupSplideMql(root);
    });
  });

  document.addEventListener("shopify:block:select", function (event) {
    var block = event.target;
    if (!block) {
      return;
    }

    var slide = block.closest(".splide__slide");
    if (!slide) {
      return;
    }

    var splideRoot = slide.closest(".splide");
    if (!splideRoot || !splideRoot._shoppySplideInstance) {
      return;
    }

    var slides = Array.prototype.slice.call(slide.parentNode.children);
    var index = slides.indexOf(slide);

    if (index > -1) {
      splideRoot._shoppySplideInstance.go(index);
    }
  });
})();

(function () {
  // =====================================================================================
  // Header sticky mode (scroll up)
  // =====================================================================================
  function initScrollUpStickyHeader() {
    var header = document.querySelector("store-header[data-sticky-mode='scroll_up']");
    if (!header) {
      return;
    }

    if (header._shoppyScrollUpStickyBound) {
      return;
    }
    header._shoppyScrollUpStickyBound = true;

    var lastY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var ticking = false;

    function setStickyHeaderEnabled(enabled) {
      document.documentElement.style.setProperty(
        "--enable-sticky-header",
        enabled ? "1" : "0",
      );
    }

    function update() {
      ticking = false;
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      var goingUp = y < lastY;

      if (y <= 0) {
        header.classList.remove("is-scroll-hidden");
        setStickyHeaderEnabled(true);
      } else {
        var hidden = !goingUp;
        header.classList.toggle("is-scroll-hidden", hidden);
        setStickyHeaderEnabled(!hidden);
      }

      lastY = y;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    update();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initScrollUpStickyHeader);
  } else {
    initScrollUpStickyHeader();
  }

  document.addEventListener("shopify:section:load", initScrollUpStickyHeader);
})();
